// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 63.8.0, D4 — idea-catalog #136's read half.
 *
 * ## What this is for
 *
 * The identity of a record whose TYPE the reader does not know. Two pieces of
 * generic infrastructure ask that question — the frontend's record-presence
 * predicate and the backend's search-row renderer — and each wrote its own
 * `_id ?? id` expression. Measured across `src/backend/src`, `src/frontend/src`
 * and `src/shared/src` on 2026-09-21 there are **67** dual-spelling identity
 * reads in **38** files; those two are the only ones whose receiver is
 * `unknown` or a `Record<string, unknown>`. The other 65 read a specific
 * module's declared shape, and D4 rules them OUT of the class deliberately:
 * forcing a site that knows its type through a generic helper would hide which
 * spelling that module chose, which is the opposite of what #136 asks for.
 *
 * ## The two call sites disagreed about PRECEDENCE, and that is the finding
 *
 * They were not two copies of one expression. They are two different
 * expressions that look alike:
 *
 * | site | order | why |
 * |---|---|---|
 * | `frontend/src/utils/record-present.ts` | `_id` then `id` | asks *"did this 200 carry a record?"*; a record the API returns always carries `_id`, and an envelope or a `{success: true}` never does |
 * | `backend/src/skills/handlers/search-module.ts` | `id` then `_id` | renders a search row; `id` is the module's DECLARED wire id and `_id` is the storage fallback, and its own docblock records that a refused `row.id` must yield nothing rather than fall through |
 *
 * Both are correct where they stand, and swapping either would change
 * behaviour. So this module does **not** impose one order. What it centralises
 * is the thing that was genuinely duplicated and genuinely wrong to duplicate:
 * **which property names count as an identity at all.** Adding a third spelling
 * is one edit here, and
 * `scripts/shared-identity-convention-census.ts` reads the same list, so the
 * declaration side and the read side cannot drift apart.
 *
 * A helper that had forced one order would have been a smaller diff and a
 * behaviour change at one of two security-adjacent sites, discovered later.
 *
 * ## R-43
 *
 * **no match.** `recordPresent` / `recordAbsent` (`frontend/src/utils/record-present.ts`)
 * answer *"did the read return a record"*, not *"what is this record's id"*,
 * and they are frontend-only while one caller is backend. `shared/src/utils/id.ts`
 * mints ids; it does not read them. `recordPresent` is refactored onto this
 * module rather than duplicated.
 */
/**
 * The spellings that name a record's OWN identity. Not foreign keys: `owner_id`
 * and `case_id` are references to other records, and a reader that treated
 * every `*_id` as an identity would resolve most of the tree to the wrong value.
 *
 * Kept in sync with `IDENTITY_PROPERTIES` in
 * `scripts/shared-identity-convention-census.ts` by a test in that census's
 * suite — the census refuses a record-shaped type declaring more than one of
 * these, so the two lists describing different sets would make the guard and
 * the reader disagree about what the guard is guarding.
 */
export const IDENTITY_PROPERTY_NAMES = ['_id', 'id'];
/** Storage-first: what a Mongo-backed read returns. */
export const STORAGE_FIRST = ['_id', 'id'];
/** Wire-first: what a module's declared DTO calls its id. */
export const WIRE_FIRST = ['id', '_id'];
/**
 * The first identity value present on `value`, in the caller's declared order.
 *
 * Returns `undefined` when `value` is not an object or carries none of the
 * names. It does **not** judge the value: a caller that needs to refuse an
 * array, a Buffer or a symbol does that itself, because what counts as a
 * renderable id is the caller's question and not this module's.
 * `search-module.ts`'s `identifierOf` is the worked example — six layers of
 * hazard rules over the value this returns.
 */
export function identityOf(value, order = STORAGE_FIRST) {
    if (value === null || typeof value !== 'object')
        return undefined;
    const rec = value;
    for (const name of order) {
        const v = rec[name];
        if (v !== undefined && v !== null)
            return v;
    }
    return undefined;
}
/**
 * Whether a read actually returned a record.
 *
 * A record the API returns always carries its own identity; an envelope, an
 * empty object and a `{success: true}` acknowledgement never do. The four
 * record-less shapes this distinguishes are tabulated in
 * `frontend/src/utils/record-present.ts`, which delegates here — that docblock
 * is the argument for why the question is identity and not
 * `Object.keys(x).length`, and it stays where the pages that learned it live.
 */
export function hasRecordIdentity(value) {
    const id = identityOf(value, STORAGE_FIRST);
    return typeof id === 'string' ? id.length > 0 : typeof id === 'number';
}
//# sourceMappingURL=record-identity.js.map